import { useInjection } from 'inversify-react';
import styled from 'styled-components';
import type {
  IFluoroAnglesCalculator,
  IFluoroAnglesCalculatorFactory,
  IFluoroAnglesIntegrationApi
} from './interfaces';
import { FluoroAnglesPlacement } from './interfaces';
import { TYPES } from './types';
import React from 'react';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import type { Theme } from '@mui/material/styles';

interface ContainerProps {
  $placement: FluoroAnglesPlacement;
  $isMaximized?: boolean;
  $withShadow?: boolean;
  theme?: Theme;
}

interface FluoroAnglesLabelsProps extends ContainerProps {
  $integrationApi: IFluoroAnglesIntegrationApi;
}

export const FluoroAnglesLabels: React.FC<FluoroAnglesLabelsProps> = (props: FluoroAnglesLabelsProps) => {
  const [raoLao, setRaoLao] = React.useState<number>(0);
  const [caudCran, setCaudCran] = React.useState<number>(0);
  const [warning, setWarning] = React.useState<boolean>(false);

  const controllerFactory = useInjection<IFluoroAnglesCalculatorFactory>(TYPES.IFluoroAnglesCalculatorFactory);

  React.useEffect(() => {
    const subscriptions = new DisposablesBasket();
    const controller: IFluoroAnglesCalculator | undefined = controllerFactory.createController(props.$integrationApi);
    subscriptions.add(
      controller.trackAngles((_raoLao: number, _caudCran: number): void => {
        setRaoLao(_raoLao);
        setCaudCran(_caudCran);
        setWarning(Math.abs(_raoLao) > 90 + 1e-4 || Math.abs(_caudCran) > 90 + 1e-4);
      })
    );
    return (): void => {
      subscriptions.dispose();
      controller?.dispose();
    };
  }, [controllerFactory, props.$integrationApi]);

  return (
    <Container {...props} className="fluoro-labels">
      <LabelContainer $warning={warning} data-testid="coronaryAngiogramLR">
        <span>
          {raoLao >= 0 ? 'LAO' : 'RAO'}: {Math.abs(raoLao).toFixed()}&deg;
        </span>
      </LabelContainer>
      <LabelContainer $warning={warning} data-testid="coronaryAngiogramCrCa">
        <span>
          {caudCran >= 0 ? 'CRAN' : 'CAUD'}: {Math.abs(caudCran).toFixed()}&deg;
        </span>
      </LabelContainer>
    </Container>
  );
};

const Container = styled.div<ContainerProps>`
  color: ${({ theme }): string => (theme.mode !== 'dark' ? theme.palette.primary.main : '#CCC')};
  font-size: 0.7em;
  text-shadow: ${({ $withShadow }): string =>
    $withShadow
      ? '0px 0px 3px #000000, 0px 0px 4px #000000, 0px 0px 6px #000000, 0px 0px 8px #000000, 0px 0px 10px #000000'
      : ''};
  font-weight: ${({ $withShadow }): string => ($withShadow ? '600' : 'initial')};
  position: absolute;
  left: ${({ $placement }): string => ($placement === FluoroAnglesPlacement.ThreeD ? '2em' : 'unset')};
  right: ${({ $placement }): string => ($placement === FluoroAnglesPlacement.Image ? '2em' : 'unset')};
  bottom: 0;
  padding-bottom: ${({ $isMaximized }): string => ($isMaximized ? '1em' : '0.3em')};
`;

const LabelContainer = styled.span<{ $warning: boolean }>`
  margin-right: 1em;
  color: ${(props): string => (props.$warning ? props.theme.palette.error.light : 'inherit')};
`;
